import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableSkeletonProps extends React.ComponentProps<'div'> {
  columnCount: number;
  rowCount?: number;
  filterCount?: number;
  cellWidths?: string[];
  withViewOptions?: boolean;
  withPagination?: boolean;
  shrinkZero?: boolean;
}

export function DataTableSkeleton({
  columnCount,
  rowCount = 10,
  filterCount = 0,
  cellWidths = ['auto'],
  withViewOptions = true,
  withPagination = true,
  shrinkZero = false,
  className,
  ...props
}: DataTableSkeletonProps) {
  const filterSkeletonKeys = Array.from(
    { length: filterCount },
    (_, index) => `filter-skeleton-${index}`,
  );
  const headerRowKeys = ['header-row'];
  const columnKeys = Array.from(
    { length: columnCount },
    (_, index) => `column-${index}`,
  );
  const bodyRowKeys = Array.from(
    { length: rowCount },
    (_, index) => `row-${index}`,
  );
  const cozyCellWidths = Array.from(
    { length: columnCount },
    (_, index) => cellWidths[index % cellWidths.length] ?? 'auto',
  );

  return (
    <div
      className={cn('flex w-full flex-col gap-2.5 overflow-auto', className)}
      {...props}
    >
      <div className='flex w-full items-center justify-between gap-2 overflow-auto p-1'>
        <div className='flex flex-1 items-center gap-2'>
          {filterSkeletonKeys.length > 0
            ? filterSkeletonKeys.map((key) => (
                <Skeleton className='h-7 w-18 border-dashed' key={key} />
              ))
            : null}
        </div>
        {withViewOptions ? (
          <Skeleton className='ml-auto hidden h-7 w-18 lg:flex' />
        ) : null}
      </div>
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {headerRowKeys.map((headerRowKey) => (
              <TableRow className='hover:bg-transparent' key={headerRowKey}>
                {columnKeys.map((columnKey, columnIndex) => (
                  <TableHead
                    key={columnKey}
                    style={{
                      width: cozyCellWidths[columnIndex],
                      minWidth: shrinkZero
                        ? cozyCellWidths[columnIndex]
                        : 'auto',
                    }}
                  >
                    <Skeleton className='h-6 w-full' />
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {bodyRowKeys.map((rowKey) => (
              <TableRow className='hover:bg-transparent' key={rowKey}>
                {columnKeys.map((columnKey, columnIndex) => (
                  <TableCell
                    key={`${rowKey}-${columnKey}`}
                    style={{
                      width: cozyCellWidths[columnIndex],
                      minWidth: shrinkZero
                        ? cozyCellWidths[columnIndex]
                        : 'auto',
                    }}
                  >
                    <Skeleton className='h-6 w-full' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {withPagination ? (
        <div className='flex w-full items-center justify-between gap-4 overflow-auto p-1 sm:gap-8'>
          <Skeleton className='h-7 w-40 shrink-0' />
          <div className='flex items-center gap-4 sm:gap-6 lg:gap-8'>
            <div className='flex items-center gap-2'>
              <Skeleton className='h-7 w-24' />
              <Skeleton className='h-7 w-18' />
            </div>
            <div className='flex items-center justify-center font-medium text-sm'>
              <Skeleton className='h-7 w-20' />
            </div>
            <div className='flex items-center gap-2'>
              <Skeleton className='hidden size-7 lg:block' />
              <Skeleton className='size-7' />
              <Skeleton className='size-7' />
              <Skeleton className='hidden size-7 lg:block' />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
